import { FieldArray, useFormikContext } from 'formik'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { FormAddAnotherButton } from './FormAddAnotherButton'
import { FormDeleteFieldButton } from './FormDeleteFieldButton'
import { SearchableSelect } from './SearchableSelect'
import { AuthContext, ROLE } from 'src/contexts/authContext'
import { CFormInput } from '@coreui/react-pro'
import { getRelevantPmvs } from 'src/services/requests/projectMaterialView'
import { GlobalVariablesContext } from 'src/contexts/globalVariablesContext'

export default function FormSelectMultiplePartners({
  partnerOptions,
  manufacturerOptions,
  readOnly,
  allBranches,
  setInitialvaluesChanged,
  initialValues,
  setIsLoading,
  mfPartnerMappings,
  setMfPartnerMappings,
  bcPartnerMappings,
  setBcPartnerMappings,
  isBackoffice = true,
}) {
  const { t } = useTranslation()
  const auth = useContext(AuthContext)
  const { setError } = useContext(GlobalVariablesContext)

  partnerOptions = partnerOptions?.map((partner) => {
    return { label: partner.name, value: partner.id }
  })
  manufacturerOptions = manufacturerOptions.map((partner) => {
    return { label: partner.name, value: partner.id }
  })
  const {
    values: { partners, branches, mfPartners },
    setFieldValue,
  } = useFormikContext()
  let partner_count = 0
  let manufacturers_count = 0

  const getBranchOptions = (partnerId) => {
    let selectedPartner = partnerOptions.filter((partner) => partner.value == partnerId)[0]
    let branches = []
    if (!selectedPartner) {
      let parent_id = allBranches.find((branch) => branch.id == partnerId).parent_id
      selectedPartner = partnerOptions.find((partner) => partner.value == parent_id)
      branches = allBranches.filter((branch) => branch.parent_id === parent_id)
    } else {
      branches = allBranches.filter((branch) => branch.parent_id === partnerId)
    }
    branches = branches.map((branch) => {
      return { label: branch?.name, value: branch?.id }
    })
    branches.unshift({ label: selectedPartner.label + ' (HQ)', value: selectedPartner.value })
    return branches
  }

  const findBCErpProjectId = (bcId) => {
    const setting = bcPartnerMappings.find((erpOption) => erpOption.partnerId === bcId)
    return setting ? setting.erpProjectId : ''
  }

  const findErpProjectId = (mfId) => {
    const setting = mfPartnerMappings.find((erpOption) => erpOption.partnerId === mfId)
    return setting ? setting.erpProjectId : ''
  }

  const findErpProjectName = (mfId) => {
    const setting = mfPartnerMappings.find((erpOption) => erpOption.partnerId === mfId)
    return setting ? setting.erpProjectName : ''
  }

  const checkExistingPMVForBC = async (index, partner, branch) => {
    try {
      if (partner && branch[index]) {
        let response
        if (partner === branch[index]) {
          response = await getRelevantPmvs(null, branch[index], null, initialValues.id)
        } else {
          response = await getRelevantPmvs(null, partner, branch[index], initialValues.id)
        }
        if (response.data.length > 0) {
          setError(t('Backoffice.Wizard.AddPartner.Type.BC.pmvExists'))
          return true
        }
      }
      return false
    } catch (error) {
      setError(error.response?.data?.error)
      return false
    }
  }

  const checkExistingPMVForMF = async (partner) => {
    try {
      if (partner) {
        const response = await getRelevantPmvs(partner, null, null, initialValues.id)
        if (response.data.length > 0) {
          setError(t('Backoffice.Wizard.AddPartner.Type.MF.pmvExists'))
          return true
        }
      }
      return false
    } catch (error) {
      setError(error.response?.data?.error)
      return false
    }
  }

  const bcERPIdOnChange = async (input) => {
    const position = parseInt(input.target.id.split('.')[1])
    let localMapper = bcPartnerMappings
    localMapper[position].erpProjectId = input.target.value
    setBcPartnerMappings(localMapper)
  }

  const mfERPIdOnChange = async (input) => {
    const position = parseInt(input.target.id.split('.')[1])
    let localMapper = mfPartnerMappings
    localMapper[position].erpProjectId = input.target.value
    setMfPartnerMappings(localMapper)
  }

  const mfERPNameOnChange = async (input) => {
    const position = parseInt(input.target.id.split('.')[1])
    let localMapper = mfPartnerMappings
    localMapper[position].erpProjectName = input.target.value
    setMfPartnerMappings(localMapper)
  }

  return (
    <>
      {(auth.role === ROLE.CUBOTOO_ADMIN ||
        auth.role === ROLE.BUILDING_CONTRACTOR_ADMIN ||
        (auth.role === ROLE.MANUFACTURER_ADMIN && !initialValues?.id)) && (
        <FieldArray
          name={'partners'}
          render={({ push, remove }) => (
            <>
              {!!partnerOptions.length &&
                partners.map((partner, index) => {
                  const options = partnerOptions
                  const hasPartnerOptions = options.map((pr) => pr.value).includes(partner)
                  partner_count =
                    partner === ''
                      ? partner_count + 1
                      : hasPartnerOptions
                      ? partner_count + 1
                      : partner_count
                  return (
                    <>
                      {((!readOnly && partner === '') || hasPartnerOptions) && (
                        <div key={index} className="w-2 h-2 position-relative">
                          <p className="spartan-font">
                            {partners.length === 1
                              ? t('Backoffice.Wizard.AddPartner.Type.BC')
                              : `${t('Backoffice.Wizard.AddPartner.Type.BC')} (${partner_count})`}
                          </p>
                          <SearchableSelect
                            className="my-2 form-control-backoffice"
                            placeholder={t('Backoffice.Wizard.AddPartner.Type.BC')}
                            name={`partners.${index}`}
                            id={`partners.${index}`}
                            options={options}
                            disabled={readOnly}
                          />
                          {partner && getBranchOptions(partner).length > 0 && (
                            <SearchableSelect
                              className="my-2 form-control-backoffice"
                              placeholder={t('Backoffice.branches.table.columns.branch')}
                              name={`branches.${index}`}
                              id={`branches.${index}`}
                              options={getBranchOptions(partner)}
                              disabled={readOnly}
                              bcCase={true}
                              bcPartnerMappings={bcPartnerMappings}
                              setBcPartnerMappings={setBcPartnerMappings}
                            />
                          )}
                          {!readOnly && partners.length > 1 && (
                            <FormDeleteFieldButton
                              index={index}
                              handleClick={async () => {
                                setIsLoading(true)
                                let check = await checkExistingPMVForBC(index, partner, branches)
                                if (!check) {
                                  remove(index)
                                  const updatedBranches = [...branches]
                                  updatedBranches.splice(index, 1)
                                  setFieldValue('branches', updatedBranches)
                                  setInitialvaluesChanged(true)
                                  const updatedBcPartnerMappings = [...bcPartnerMappings]
                                  updatedBcPartnerMappings.splice(idx, 1)
                                  setBcPartnerMappings(updatedBcPartnerMappings)
                                }
                                setIsLoading(false)
                              }}
                            />
                          )}
                          <div>
                            <p className="w-2 h-2 position-relative">
                              <CFormInput
                                floatingLabel={'ERP Project ID'}
                                type={'text'}
                                disabled={readOnly && isBackoffice}
                                id={`bcErpId.${index}`}
                                placeholder={'ERP Project ID'}
                                name={`bcErpId.${index}`}
                                defaultValue={findBCErpProjectId(partner)}
                                onChange={(e) => bcERPIdOnChange(e)}
                              />
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )
                })}
              {!readOnly && <FormAddAnotherButton push={push} values={''} />}
            </>
          )}
        />
      )}
      {(auth.role === ROLE.CUBOTOO_ADMIN || auth.role === ROLE.MANUFACTURER_ADMIN) && (
        <FieldArray
          name={'mfPartners'}
          render={({ push, remove }) => (
            <>
              {!!manufacturerOptions.length &&
                mfPartners.map((mfPartner, idx) => {
                  const options = manufacturerOptions
                  const hasMfPartnerOptions = options.map((pr) => pr.value).includes(mfPartner)
                  manufacturers_count =
                    mfPartner === ''
                      ? manufacturers_count + 1
                      : hasMfPartnerOptions
                      ? manufacturers_count + 1
                      : manufacturers_count
                  return (
                    <>
                      {((!readOnly && mfPartner === '') || hasMfPartnerOptions) && (
                        <div key={idx} className="w-2 h-2 position-relative">
                          <p className="spartan-font">
                            {mfPartners.length === 1
                              ? t('Backoffice.Wizard.AddPartner.Type.MF')
                              : `${t(
                                  'Backoffice.Wizard.AddPartner.Type.MF',
                                )} (${manufacturers_count})`}
                          </p>
                          <SearchableSelect
                            className="my-2 form-control-backoffice"
                            placeholder={t('Backoffice.Wizard.AddPartner.Type.MF')}
                            name={`mfPartners.${idx}`}
                            id={`mfPartners.${idx}`}
                            options={options}
                            disabled={readOnly}
                            mfCase={true}
                            mfPartnerMappings={mfPartnerMappings}
                            setMfPartnerMappings={setMfPartnerMappings}
                          />
                          {!readOnly && mfPartners.length > 1 && (
                            <FormDeleteFieldButton
                              index={idx}
                              handleClick={async () => {
                                setIsLoading(true)
                                let check = await checkExistingPMVForMF(mfPartner)
                                if (!check) {
                                  remove(idx)
                                  const updatedMfPartners = [...mfPartners]
                                  updatedMfPartners.splice(idx, 1)
                                  setFieldValue('mfPartners', updatedMfPartners)
                                  setInitialvaluesChanged(true)
                                  const updatedMfPartnerMappings = [...mfPartnerMappings]
                                  updatedMfPartnerMappings.splice(idx, 1)
                                  setMfPartnerMappings(updatedMfPartnerMappings)
                                }
                                setIsLoading(false)
                              }}
                            />
                          )}
                          <div>
                            <p className="w-2 h-2 position-relative">
                              <CFormInput
                                floatingLabel={'ERP Project ID'}
                                type={'text'}
                                disabled={readOnly && isBackoffice}
                                id={`mfErpId.${idx}`}
                                placeholder={'ERP Project ID'}
                                name={`mfErpId.${idx}`}
                                defaultValue={findErpProjectId(mfPartner)}
                                onChange={(e) => mfERPIdOnChange(e)}
                              />
                            </p>
                            <p className="w-2 h-2 position-relative">
                              <CFormInput
                                floatingLabel={'ERP Project Name'}
                                type={'text'}
                                disabled={readOnly && isBackoffice}
                                id={`mfErpName.${idx}`}
                                placeholder={'ERP Project Name'}
                                name={`mfErpName.${idx}`}
                                defaultValue={findErpProjectName(mfPartner)}
                                onChange={(e) => mfERPNameOnChange(e)}
                              />
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )
                })}
            </>
          )}
        />
      )}
    </>
  )
}
